import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Button, Dialog, Input } from '@/design-system';
import type { FunnelDraft } from '@/domain/ai/funnel-draft';
import { navigate } from '@/app/router';
import * as dashboardApi from '../api/dashboard-api';
import { getNodeDefinition } from '@/features/funnel-editor/nodes/registry/node-registry';

export function CreateWithAIDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [description, setDescription] = useState('');
  const [draft, setDraft] = useState<FunnelDraft | null>(null);
  const [name, setName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const controller = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!open) {
      controller.current?.abort();
      setDescription('');
      setDraft(null);
      setName('');
      setError(null);
      setGenerating(false);
    }
  }, [open]);

  async function generate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextController = new AbortController();
    controller.current = nextController;
    setGenerating(true);
    setDraft(null);
    setError(null);
    try {
      const result = await dashboardApi.generateFunnelDraft(description.trim(), nextController.signal);
      if (nextController.signal.aborted) return;
      setDraft(result.draft);
      setName(result.draft.name);
    } catch (cause) {
      if (!nextController.signal.aborted) {
        setError(cause instanceof Error ? cause.message : 'Não foi possível gerar a proposta. Tente novamente.');
      }
    } finally {
      if (controller.current === nextController) setGenerating(false);
    }
  }

  async function create() {
    if (!draft || !name.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const result = await dashboardApi.createFunnelFromDraft(draft, name.trim());
      onClose();
      navigate(`/app/funnels/${result.funnel.id}`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Não foi possível criar o funil. Tente novamente.');
    } finally {
      setCreating(false);
    }
  }

  return (
    <Dialog open={open} onClose={() => { if (!creating) onClose(); }} title="Novo funil com IA" className="max-h-[90vh] max-w-2xl overflow-y-auto">
      <form onSubmit={(event) => void generate(event)} className="flex flex-col gap-3">
        <label htmlFor="ai-funnel-description" className="text-sm text-text-secondary">Descreva o funil que deseja montar</label>
        <textarea
          id="ai-funnel-description"
          autoFocus
          required
          minLength={20}
          maxLength={2000}
          rows={5}
          value={description}
          onChange={(event) => { setDescription(event.target.value); setDraft(null); }}
          disabled={generating || creating}
          placeholder="Ex.: Produto de baixo valor com tráfego vindo das redes sociais para uma página de vendas, captura de leads, WhatsApp e pagamento."
          className="w-full resize-y rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus-visible:border-border-brand"
        />
        <p className="text-xs text-text-muted">A IA propõe a estrutura inicial. Você revisa antes de criar e pode editar tudo no canvas.</p>
        <div className="flex justify-end">
          <Button type="submit" disabled={generating || creating || description.trim().length < 20}>
            {generating ? 'Gerando proposta…' : draft ? 'Gerar novamente' : 'Gerar proposta'}
          </Button>
        </div>
      </form>

      {draft ? (
        <section aria-label="Prévia do funil" className="mt-5 space-y-4 border-t border-border pt-4">
          <Input label="Nome do funil" value={name} maxLength={120} required onChange={(event) => setName(event.target.value)} disabled={creating} />
          <p className="text-sm text-text-secondary">{draft.summary}</p>
          <div>
            <h3 className="mb-2 text-sm font-medium text-text-primary">Etapas ({draft.nodes.length})</h3>
            <ol className="max-h-48 space-y-2 overflow-y-auto text-sm text-text-secondary">
              {draft.nodes.map((node) => <li key={node.key} className="rounded border border-border bg-surface px-3 py-2"><span className="text-text-primary">{node.name}</span> <span className="text-text-muted">· {getNodeDefinition(node.type)?.label ?? node.type}</span><div className="text-xs text-text-muted">{node.description}</div></li>)}
            </ol>
          </div>
          <div>
            <h3 className="mb-2 text-sm font-medium text-text-primary">Conexões ({draft.edges.length})</h3>
            <ul className="max-h-32 space-y-1 overflow-y-auto text-xs text-text-secondary">
              {draft.edges.map((edge, index) => <li key={`${edge.sourceKey}-${edge.targetKey}-${index}`}>{draft.nodes.find((node) => node.key === edge.sourceKey)?.name} → {draft.nodes.find((node) => node.key === edge.targetKey)?.name}{edge.label ? ` · ${edge.label}` : ''}</li>)}
            </ul>
          </div>
          {draft.caveats.length > 0 ? <div className="text-xs text-text-muted"><h3 className="font-medium">Para validar no editor</h3><ul className="list-inside list-disc">{draft.caveats.map((caveat, index) => <li key={index}>{caveat}</li>)}</ul></div> : null}
        </section>
      ) : null}
      {error ? <p role="alert" className="mt-4 text-sm text-[#f08484]">{error}</p> : null}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={creating}>Cancelar</Button>
        {draft ? <Button onClick={() => void create()} disabled={creating || generating || !name.trim()}>{creating ? 'Criando…' : 'Criar funil e abrir no editor'}</Button> : null}
      </div>
    </Dialog>
  );
}
